import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import openai from '../../../services/openaiClient';
import { Users, Sparkles, Target, UserCheck, TrendingUp, FileUser } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const ClientsActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('insights');
  const [loading, setLoading] = useState(false);
  const [aiInsight, setAiInsight] = useState('');
  const [segment, setSegment] = useState('');

  const analyzeClients = async (analysisType) => {
    setLoading(true);
    try {
      const prompts = {
        behavior: 'قم بتحليل سلوك العملاء وأنماط الشراء وقدم استراتيجيات لتحسين التفاعل',
        retention: 'قم بتحليل معدلات الاحتفاظ بالعملاء وقدم توصيات لتحسين ولاء العملاء',
        acquisition: 'قم بتحليل استراتيجيات اكتساب العملاء الجدد وحدد القنوات الأكثر فعالية',
        satisfaction: 'قم بتحليل مستويات رضا العملاء وحدد المجالات التي تحتاج إلى تحسين',
        segment: `قم بتحليل شريحة العملاء: ${segment} وقدم رؤى واستراتيجيات مخصصة`
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'أنت محلل علاقات عملاء متخصص في تحليل سلوك العملاء وتقديم استراتيجيات لتحسين التفاعل والاحتفاظ.'
          },
          {
            role: 'user',
            content: prompts?.[analysisType]
          }
        ],
        max_tokens: 500,
        temperature: 0.7
      });

      setAiInsight(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error analyzing clients:', error);
      setAiInsight('حدث خطأ في التحليل. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'insights', label: 'رؤى العملاء', icon: Sparkles },
    { id: 'segments', label: 'التقسيمات', icon: Target },
    { id: 'reports', label: 'التقارير', icon: FileUser }
  ];

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="تقارير العملاء - تحليلات متقدمة" 
      size="large"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="flex border-b border-border mb-6">
        {tabs?.map((tab) => {
          const Icon = tab?.icon;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab?.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'insights' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={() => analyzeClients('behavior')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Users className="w-6 h-6" />
              <span>تحليل سلوك العملاء</span>
            </Button>
            <Button
              onClick={() => analyzeClients('retention')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <UserCheck className="w-6 h-6" />
              <span>تحليل الاحتفاظ بالعملاء</span>
            </Button>
            <Button
              onClick={() => analyzeClients('acquisition')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <TrendingUp className="w-6 h-6" />
              <span>تحليل اكتساب العملاء</span>
            </Button>
            <Button
              onClick={() => analyzeClients('satisfaction')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Sparkles className="w-6 h-6" />
              <span>تحليل رضا العملاء</span>
            </Button>
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="شريحة العملاء (مثال: VIP، مستثمرون، مستأجرون)"
              value={segment}
              onChange={(e) => setSegment(e?.target?.value)}
              className="flex-1"
            />
            <Button
              onClick={() => analyzeClients('segment')}
              disabled={loading || !segment}
            >
              <Target className="w-4 h-4 ml-2" />
              تحليل
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="mr-3 text-muted-foreground">جاري التحليل...</span>
            </div>
          )}

          {aiInsight && !loading && (
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-warning" />
                <h3 className="font-semibold text-foreground">رؤى ذكية</h3>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{aiInsight}</p>
            </div>
          )}
        </div>
      )}
      {activeTab === 'segments' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'عملاء VIP', count: '٤٥', percentage: '٩٪', color: 'text-yellow-500' },
              { title: 'مستثمرون', count: '١٢٨', percentage: '٢٦٪', color: 'text-blue-500' },
              { title: 'مستأجرون', count: '٢٣٤', percentage: '٤٨٪', color: 'text-green-500' },
              { title: 'مشترون', count: '٨٢', percentage: '١٧٪', color: 'text-purple-500' }
            ]?.map((seg, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4 hover:border-warning transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-foreground">{seg?.title}</h4>
                  <span className={`text-2xl font-bold ${seg?.color}`}>{seg?.count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">من إجمالي العملاء</span>
                  <span className="text-sm font-medium text-foreground">{seg?.percentage}</span>
                </div>
                <Button size="sm" variant="outline" className="w-full mt-3">عرض التفاصيل</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'تقرير النشاط', desc: 'نشاط العملاء والتفاعلات' },
              { title: 'تقرير القيمة الدائمة', desc: 'القيمة الدائمة للعميل (LTV)' },
              { title: 'تقرير التحويل', desc: 'معدلات التحويل حسب المصدر' },
              { title: 'تقرير الولاء', desc: 'مستويات ولاء العملاء' }
            ]?.map((report, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4 hover:border-warning transition-colors cursor-pointer">
                <h4 className="font-medium text-foreground mb-1">{report?.title}</h4>
                <p className="text-xs text-muted-foreground mb-3">{report?.desc}</p>
                <Button size="sm" variant="outline">إنشاء التقرير</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ClientsActionsModal;